/**
 * The note half of the composer's entry points.
 *
 * `useOpenNoteCompose` owns the `ahoy_note` create permission and the
 * `openMemoCompose` call shape, so every "Add note" affordance — the record
 * header, the contact hover card, the dashboard's meeting rows, the Notes tab —
 * opens the same composer under the same gate instead of re-deriving both.
 *
 * `useNoteTabActions` binds that to ONE host record: it backs both a record's
 * Notes tab (the tab strip's create button, rendered by CRMObjectFullPageView
 * via `tabActions`) and the list's empty-state "Create your first note" button
 * (RecordNotesListTab), so the composer is opened with identical linked-entity
 * context from either. The tab's "Add task" comes from the twin
 * `useTaskTabActions` — notes no longer carry a due, so there is no "reminder"
 * variant of a note to create.
 */

import { useCallback, useMemo } from 'react';

import type { MemoComposeLinkedEntity } from '@/contexts/MemoComposeContext';
import { useMemoCompose } from '@/contexts/MemoComposeContext';
import { usePermissions } from '@/hooks/usePermissions';

export interface OpenNoteCompose {
  /** Open the composer for a plain note, optionally pre-linked to records. */
  openNoteCompose: (options?: {
    linkedEntities?: MemoComposeLinkedEntity[];
  }) => void;
  /** Whether the current user may create notes at all. */
  canCreateNote: boolean;
}

export function useOpenNoteCompose(): OpenNoteCompose {
  const { openMemoCompose } = useMemoCompose();
  const { hasPermission } = usePermissions();
  const canCreateNote = hasPermission('ahoy_note', 'ahoy_object_created');

  const openNoteCompose = useCallback(
    (options?: { linkedEntities?: MemoComposeLinkedEntity[] }) => {
      openMemoCompose(options);
    },
    [openMemoCompose]
  );

  return useMemo(
    () => ({ openNoteCompose, canCreateNote }),
    [openNoteCompose, canCreateNote]
  );
}

export interface NoteTabActions {
  /** Open the composer for a plain note pre-linked to the host record. */
  startCreatingNote: () => void;
  /** Whether the current user may create notes on this record. */
  canCreateNote: boolean;
}

export function useNoteTabActions(
  entityId: string,
  hostEntityType: string
): NoteTabActions {
  const { openNoteCompose, canCreateNote } = useOpenNoteCompose();

  const startCreatingNote = useCallback(() => {
    openNoteCompose({
      linkedEntities: [{ entityId, entityTypeName: hostEntityType }],
    });
  }, [openNoteCompose, entityId, hostEntityType]);

  return useMemo(
    () => ({ startCreatingNote, canCreateNote }),
    [startCreatingNote, canCreateNote]
  );
}
